/**
 * Icon Alignment Store
 * Icon alignment for icon-mode surfaces: the minimized (icon-mode) sidepanels
 * and the toolbar rail. Each surface remembers its own alignment —
 * 'start' (left/top), 'center' or 'end' (right/bottom) — and the icons pack
 * toward that direction inside the rail.
 *
 * Defaults: sidepanels center (icon rails read best centered), the toolbar
 * start (Photoshop packs its tool rail toward the screen edge). Persisted
 * under the panel storage namespace. Lazy singleton — see useDockLayoutStore.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { debouncedLocalStorage } from './persistStorage';
import { panelStorageKey } from './namespace';

/** Icon alignment values: 'start' = leading edge, 'end' = trailing edge of
    the icon rail. The rendered axis depends on the surface's orientation —
    a vertical (left/right-docked) rail packs top/center/bottom, a horizontal
    (top/bottom-docked) rail packs left/center/right. */
export type IconAlignment = 'start' | 'center' | 'end';

/** Surface keys: the fixed 'toolbar' rail plus one entry per sidepanel id */
export type IconAlignmentSurface = string; // 'toolbar' | sidepanel instance ids

const DEFAULT_SURFACE_ALIGNMENT: Record<string, IconAlignment> = {
    toolbar: 'start',
};

interface IconAlignmentState {
    /** Per-surface icon alignment (sidepanel ids / 'toolbar') */
    alignments: Record<IconAlignmentSurface, IconAlignment>;
    setIconAlignment: (surface: IconAlignmentSurface, alignment: IconAlignment) => void;
    getIconAlignment: (surface: IconAlignmentSurface) => IconAlignment;
}

type IconAlignmentStoreApi = ReturnType<typeof createIconAlignmentStore>;

/** Sidepanels default to center; the toolbar defaults to start (left/top) */
export const getSurfaceDefaultAlignment = (
    surface: IconAlignmentSurface,
): IconAlignment =>
    surface === 'toolbar' ? 'start' : DEFAULT_SURFACE_ALIGNMENT[surface] ?? 'center';

const createIconAlignmentStore = () =>
    create<IconAlignmentState>()(
        persist(
            (set, get) => ({
                alignments: {},

                setIconAlignment: (surface, alignment) =>
                    set((state) => ({
                        alignments: { ...state.alignments, [surface]: alignment },
                    })),

                getIconAlignment: (surface) =>
                    get().alignments[surface] ?? getSurfaceDefaultAlignment(surface),
            }),
            {
                name: panelStorageKey('icon_alignment'),
                storage: createJSONStorage(() => debouncedLocalStorage()),
                partialize: (state) => ({ alignments: state.alignments }),
            },
        ),
    );

let instance: IconAlignmentStoreApi | null = null;
const getStore = (): IconAlignmentStoreApi =>
    (instance ??= createIconAlignmentStore());

/** Namespace-lazy singleton: plain hook + static accessor shape preserved. */
export const useIconAlignmentStore: IconAlignmentStoreApi = ((...args: unknown[]) =>
    (getStore() as unknown as (...a: unknown[]) => unknown)(...args)) as IconAlignmentStoreApi;
useIconAlignmentStore.getState = () => getStore().getState();
useIconAlignmentStore.setState = ((...args: unknown[]) =>
    (
        getStore().setState as unknown as (...a: unknown[]) => unknown
    )(...args)) as IconAlignmentStoreApi['setState'];
useIconAlignmentStore.subscribe = ((...args: unknown[]) =>
    (
        getStore().subscribe as unknown as (...a: unknown[]) => unknown
    )(...args)) as IconAlignmentStoreApi['subscribe'];

/** Non-hook accessor for plain-TS consumers */
export const readIconAlignment = (
    surface: IconAlignmentSurface,
): IconAlignment => useIconAlignmentStore.getState().getIconAlignment(surface);

/** CSS class for a surface's alignment: icon-alignment-start/center/end */
export const iconAlignmentClass = (surface: IconAlignmentSurface): string =>
    `icon-alignment-${readIconAlignment(surface)}`;
